import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { BaseURL, apiHeader } from '../../config/apiUrl';
import { Get, Patch, Post } from '../../Axios/AxiosFunctions';
import Modal from './Modal';
import { inDays, isoDay } from './tone';
import classes from './v2.module.css';

const ok = (r) => !!r && !r.isAxiosError && r.status >= 200 && r.status < 300;

/** Search contacts or listings by name, for the task editor. */
function Picker({ kind, placeholder, onPick }) {
  const { access_token: accessToken, company_id: companyId } = useSelector((s) => s.authReducer);
  const [q, setQ] = useState('');
  const [list, setList] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setList([]);
    const t = setTimeout(
      () => Get(BaseURL(`my-tasks/lookup?q=${encodeURIComponent(q)}`), accessToken, companyId, false).then((r) => setList(r?.data?.data?.[kind] || [])),
      300,
    );
    return () => clearTimeout(t);
  }, [q, kind, accessToken, companyId]);
  return (
    <div style={{ position: 'relative' }}>
      <input className={classes.input} placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} />
      {list.length > 0 && (
        <div className={classes.card} style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, top: '100%', padding: 4, marginTop: 2 }}>
          {list.map((x) => (
            <button
              key={x.id}
              type="button"
              className={classes.pickRow}
              style={{ width: '100%' }}
              onClick={() => {
                onPick(x);
                setQ('');
                setList([]);
              }}
            >
              <b>{x.name}</b>
              <span className={classes.small}>{kind === 'contacts' ? x.email : x.status}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Add or edit a task (My tasks, the listing Dashboard, Interests). Every field
 * is editable: what it is, when it is due, who does it, who it is about, and
 * which listing (Adam, 16 Oct: contacts, listing and staff, all optional).
 * A listing task stays on its listing's project, so its listing is fixed.
 */
export default function TaskEditor({ task, preset = {}, staff = [], onClose, onSaved }) {
  const { access_token: accessToken, company_id: companyId, user } = useSelector((s) => s.authReducer);
  const isNew = !task;
  const [f, setF] = useState(() => ({
    title: task?.title || preset.title || '',
    due: task ? isoDay(task.due) : preset.due || inDays(7),
    assignees: task?.assignees || preset.assignees || (user ? [{ id: user._id, name: [user.firstName, user.lastName].filter(Boolean).join(' ') }] : []),
    contacts: task?.contacts || preset.contacts || [],
    listing: task?.listing || preset.listing || null,
    done: !!task?.done,
    type: task?.type || preset.type || 'other',
    importance: task?.importance || preset.importance || 'green',
  }));
  const [busy, setBusy] = useState(false);
  const listingFixed = task?.source === 'task';

  const save = async () => {
    if (!f.title.trim()) return;
    setBusy(true);
    const body = {
      title: f.title,
      due: f.due || null,
      assignees: f.assignees.map((a) => a.id),
      contactIds: f.contacts.map((c) => c.id),
      ...(listingFixed ? {} : { businessId: f.listing?.id || null }),
      ...(isNew ? {} : { done: f.done }),
      ...(preset.leadId ? { leadId: preset.leadId } : {}),
      // Type and importance are To-Do page fields (listing tasks do not have them).
      ...(task?.source === 'task' ? {} : { type: f.type, importance: f.importance }),
    };
    const r = isNew
      ? await Post(BaseURL('my-tasks'), body, apiHeader(accessToken), companyId)
      : await Patch(BaseURL(`my-tasks/${task.source}/${task.id}`), body, apiHeader(accessToken), companyId);
    setBusy(false);
    if (ok(r)) {
      const res = r.data?.data;
      toast.success(
        isNew
          ? res?.source === 'task'
            ? `Added to ${f.listing?.name}'s tasks`
            : res?.noProject
            ? 'Added to To-do (this listing has no project yet)'
            : 'Task added'
          : 'Task saved',
      );
      onSaved && onSaved(res);
      onClose();
    }
  };

  const chip = (x, remove) => (
    <span key={x.id} className={classes.taskChip}>
      {x.name}
      <button type="button" aria-label={`Remove ${x.name}`} onClick={remove}>
        ×
      </button>
    </span>
  );
  const freeStaff = staff.filter((s) => !f.assignees.some((a) => a.id === s.id));

  return (
    <Modal
      title={isNew ? 'New to-do' : task?.source === 'task' ? 'Edit listing task' : 'Edit to-do'}
      width={620}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={classes.btn} disabled={busy || !f.title.trim()} onClick={save}>
            {busy ? 'Saving…' : isNew ? 'Add' : 'Save'}
          </button>
          <button type="button" className={`${classes.btn} ${classes.btnGhost}`} onClick={onClose}>
            Cancel
          </button>
          {task?.source && (
            <span className={classes.help} style={{ marginTop: 0 }}>
              {task.source === 'task' ? `On the ${task.listing?.name || 'listing'} project${task.stage ? `, ${task.stage}` : ''}` : 'On the To-do list'}
            </span>
          )}
        </>
      }
    >
      <div className={classes.field}>
        <label htmlFor="te-title">What to do</label>
        <input id="te-title" autoFocus className={classes.input} value={f.title} maxLength={300} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Call the landlord about the lease" />
      </div>
      <div className={classes.fields} style={{ marginTop: 10 }}>
        <div className={classes.field}>
          <label htmlFor="te-due">Due</label>
          <input id="te-due" type="date" className={classes.input} value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} />
        </div>
        {task?.source !== 'task' && (
          <div className={classes.field}>
            <label htmlFor="te-type">Kind</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <select id="te-type" className={classes.select} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                {[
                  ['other', 'To-do'],
                  ['call', 'Call'],
                  ['email', 'Email'],
                  ['schedule a meeting', 'Meeting'],
                  ['agenda', 'Agenda'],
                  ['note', 'Note'],
                ].map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <select className={classes.select} value={f.importance} onChange={(e) => setF({ ...f, importance: e.target.value })} aria-label="Importance">
                <option value="green">Normal</option>
                <option value="lemon">Important</option>
                <option value="red">Urgent</option>
                <option value="gray">Low</option>
              </select>
            </div>
          </div>
        )}
        {!isNew && (
          <div className={classes.field}>
            <label>Status</label>
            <label className={classes.small} style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
              <input type="checkbox" checked={f.done} onChange={(e) => setF({ ...f, done: e.target.checked })} /> Done
            </label>
          </div>
        )}
      </div>
      <div className={classes.field} style={{ marginTop: 10 }}>
        <label>Who does it</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {f.assignees.map((a) => chip(a, () => setF({ ...f, assignees: f.assignees.filter((x) => x.id !== a.id) })))}
          {freeStaff.length > 0 && (
            <select className={classes.select} style={{ width: 'auto' }} value="" onChange={(e) => {
              const s = staff.find((x) => x.id === e.target.value);
              if (s) setF({ ...f, assignees: [...f.assignees, s] });
            }}>
              <option value="">+ Add a person</option>
              {freeStaff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className={classes.field} style={{ marginTop: 10 }}>
        <label>Listing (optional)</label>
        {f.listing ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {listingFixed ? <span className={classes.taskChip}>{f.listing.name}</span> : chip(f.listing, () => setF({ ...f, listing: null }))}
            {listingFixed && <span className={classes.help} style={{ marginTop: 0 }}>A listing task stays on its project.</span>}
          </div>
        ) : (
          <Picker kind="listings" placeholder="Search listings" onPick={(x) => setF({ ...f, listing: x })} />
        )}
      </div>
      <div className={classes.field} style={{ marginTop: 10 }}>
        <label>About (optional)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: f.contacts.length ? 6 : 0 }}>
          {f.contacts.map((c) => chip(c, () => setF({ ...f, contacts: f.contacts.filter((x) => x.id !== c.id) })))}
        </div>
        <Picker kind="contacts" placeholder="Search buyers, sellers and contacts" onPick={(x) => !f.contacts.some((c) => c.id === x.id) && setF({ ...f, contacts: [...f.contacts, x] })} />
      </div>
    </Modal>
  );
}
